import React, { useState, useEffect } from 'react';
import {
  getPerformanceSummary,
  getSectionMetrics,
  predictExamReadiness,
  getPeerComparison,
  getCommonMisconceptions,
  getStreak,
} from '../utils/analytics';
import { MBTA_SECTIONS, EXTRA_SECTIONS, SECTION_META } from '../data/index';
import '../styles/AnalyticsDashboard.css';

export default function AnalyticsDashboard({ onHome }) {
  const [summary, setSummary] = useState(null);
  const [selectedSection, setSelectedSection] = useState(MBTA_SECTIONS[0]);
  const [sectionMetrics, setSectionMetrics] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [peerComparison, setPeerComparison] = useState(null);
  const [misconceptions, setMisconceptions] = useState([]);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    const perf = getPerformanceSummary();
    setSummary(perf);
    setStreak(getStreak());
  }, []);

  useEffect(() => {
    if (selectedSection) {
      const metrics = getSectionMetrics(selectedSection);
      setSectionMetrics(metrics);

      const ready = predictExamReadiness(selectedSection);
      setReadiness(ready);

      const peer = getPeerComparison(selectedSection);
      setPeerComparison(peer);
    }
  }, [selectedSection]);

  useEffect(() => {
    const misc = getCommonMisconceptions();
    setMisconceptions(misc);
  }, []);

  if (!summary) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">
          <p>No data yet. Complete some practice or exam sessions to see analytics.</p>
          <button className="btn-primary" onClick={onHome}>Back to Home</button>
        </div>
      </div>
    );
  }

  const meta = SECTION_META[selectedSection];

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <button className="btn-back" onClick={onHome}>← Back</button>
        <h1>📊 Performance Analytics</h1>
        <div className="analytics-meta">
          <div className="analytics-stat">
            <span className="stat-label">Total Attempts</span>
            <span className="stat-value">{summary.totalAttempts}</span>
          </div>
          <div className="analytics-stat">
            <span className="stat-label">Overall Score</span>
            <span className="stat-value" style={{ color: summary.overallScore >= 80 ? '#10b981' : '#f59e0b' }}>
              {summary.overallScore}%
            </span>
          </div>
          <div className="analytics-stat">
            <span className="stat-label">Study Streak</span>
            <span className="stat-value">🔥 {streak} days</span>
          </div>
          <div className="analytics-stat">
            <span className="stat-label">Mastered Questions</span>
            <span className="stat-value">{summary.masteredQuestions}</span>
          </div>
        </div>
      </div>

      <div className="analytics-content">
        {/* Section Selector */}
        <div className="analytics-section-selector">
          <h3>Select Section</h3>
          <div className="section-buttons">
            {[...MBTA_SECTIONS, ...EXTRA_SECTIONS].map(section => (
              <button
                key={section}
                className={`section-btn ${selectedSection === section ? 'active' : ''}`}
                style={{
                  borderColor: SECTION_META[section].borderColor,
                  background: selectedSection === section ? SECTION_META[section].lightColor : 'white',
                }}
                onClick={() => setSelectedSection(section)}
              >
                {SECTION_META[section].icon} {section}
              </button>
            ))}
          </div>
        </div>

        {/* Section Metrics */}
        {sectionMetrics && (
          <div className="analytics-grid">
            {/* Performance Card */}
            <div className="analytics-card" style={{ borderColor: meta.borderColor }}>
              <div className="card-header" style={{ background: meta.lightColor }}>
                <h3>{meta.icon} {selectedSection} Performance</h3>
              </div>
              <div className="card-body">
                <div className="metric-row">
                  <span>Average Score</span>
                  <span className="metric-value" style={{ color: meta.color }}>
                    {sectionMetrics.avgScore}%
                  </span>
                </div>
                <div className="metric-row">
                  <span>Total Attempts</span>
                  <span className="metric-value">{sectionMetrics.totalAttempts}</span>
                </div>
                <div className="metric-row">
                  <span>Mastered Questions</span>
                  <span className="metric-value">{sectionMetrics.masteredCount}/{sectionMetrics.totalQuestions}</span>
                </div>
                <div className="metric-row">
                  <span>Struggling Questions</span>
                  <span className="metric-value" style={{ color: '#ef4444' }}>
                    {sectionMetrics.strugglingQuestions}
                  </span>
                </div>
                {/* Progress Bar */}
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${sectionMetrics.avgScore}%`,
                        background: meta.color,
                      }}
                    />
                  </div>
                  <small>{sectionMetrics.avgScore}% of 80% to pass</small>
                </div>
              </div>
            </div>

            {/* Readiness Card */}
            {readiness && (
              <div className="analytics-card">
                <div className="card-header" style={{ background: '#eff6ff' }}>
                  <h3>🎯 Exam Readiness</h3>
                </div>
                <div className="card-body">
                  <div className="readiness-badge" style={{
                    background: readiness.readiness === 'ready' ? '#10b981' :
                               readiness.readiness === 'almost' ? '#f59e0b' : '#ef4444',
                  }}>
                    {readiness.readiness === 'ready' ? '✓ READY TO TEST' :
                     readiness.readiness === 'almost' ? '⚠ ALMOST READY' : '📚 KEEP STUDYING'}
                  </div>
                  <div className="metric-row">
                    <span>Current Score</span>
                    <span className="metric-value">{readiness.score}%</span>
                  </div>
                  {readiness.readiness !== 'ready' && (
                    <>
                      <div className="metric-row">
                        <span>Score Gap</span>
                        <span className="metric-value">{readiness.gap}%</span>
                      </div>
                      <div className="metric-row">
                        <span>Estimated Days to Ready</span>
                        <span className="metric-value">{readiness.daysToReady}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Peer Comparison Card */}
            {peerComparison && (
              <div className="analytics-card">
                <div className="card-header" style={{ background: '#f5f3ff' }}>
                  <h3>👥 Peer Comparison</h3>
                </div>
                <div className="card-body">
                  <div className="percentile-badge" style={{
                    background: peerComparison.percentile >= 70 ? '#10b981' :
                               peerComparison.percentile >= 50 ? '#3b82f6' : '#f59e0b',
                  }}>
                    {peerComparison.percentile}th Percentile
                  </div>
                  <div className="metric-row">
                    <span>Your Score</span>
                    <span className="metric-value">{peerComparison.yourScore}%</span>
                  </div>
                  <div className="metric-row">
                    <span>Peer Average</span>
                    <span className="metric-value">{peerComparison.peerAverage}%</span>
                  </div>
                  <div className="metric-row">
                    <span>Difference</span>
                    <span className="metric-value" style={{
                      color: peerComparison.aheadBy > 0 ? '#10b981' : '#ef4444'
                    }}>
                      {peerComparison.aheadBy > 0 ? '+' : ''}{peerComparison.aheadBy}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Common Misconceptions */}
        {misconceptions.length > 0 && (
          <div className="analytics-card full-width">
            <div className="card-header" style={{ background: '#fef2f2' }}>
              <h3>⚠️ Common Misconceptions (Most Missed Questions)</h3>
            </div>
            <div className="card-body">
              <div className="misconceptions-list">
                {misconceptions.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="misconception-item">
                    <span className="mis-rank">#{idx + 1}</span>
                    <div className="mis-info">
                      <span className="mis-section">{item.section}</span>
                      <span className="mis-rate">Correct Rate: {item.correctRate}%</span>
                    </div>
                    <span className="mis-attempts">{item.attempts} attempts</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
